"use client";

import { useIntl, type MessageDescriptor } from "./intl-context.js";

type TranslationParams = MessageDescriptor & {
  values?: Record<string, string>;
};

/**
 * Translation hook that gracefully degrades when no I18nProvider is present.
 *
 * - With I18nProvider: Uses react-intl's formatMessage for full i18n support (react-intl will be bundled)
 * - Without I18nProvider: Returns the defaultMessage with simple string interpolation (no react-intl bundled)
 */
export function useTranslation() {
  const i18n = useIntl();

  return (params: TranslationParams): string => {
    const { defaultMessage, description, id, values } = params;

    // If I18nProvider is present, use react-intl's formatMessage
    if (i18n) {
      // TODO: Do we really want to cast this to a string?
      return String(
        i18n.formatMessage({ id, defaultMessage, description }, values),
      );
    }

    // If no I18nProvider is present, return defaultMessage with simple interpolation
    if (!values) {
      return defaultMessage;
    }

    // Simple string interpolation for values
    return defaultMessage.replace(/\{(\w+)\}/g, (match, key) => {
      return key in values ? String(values[key]) : match;
    });
  };
}
